import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import ResponsiveDiagram from './ResponsiveDiagram';

const StairAngle: React.FC = () => {
  const [rise, setRise] = useState('');
  const [run, setRun] = useState('');
  const [angle, setAngle] = useState<string | null>(null);
  const [hypoLength, setHypoLength] = useState<string | null>(null);
  const [riseRunSum, setRiseRunSum] = useState<string | null>(null);
  const [twoRiseRunSum, setTwoRiseRunSum] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([rise, run]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!rise || !run) {
      setShowError(true);
      return;
    }

    const riseNum = parseFloat(rise);
    const runNum = parseFloat(run);

    if (isNaN(riseNum) || isNaN(runNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // Calculate angle using atan (rise/run)
    const calculatedAngle = Math.atan(riseNum / runNum) * 180 / Math.PI;
    const roundedAngle = Math.round(calculatedAngle * 1000) / 1000;

    // Calculate hypotenuse length using Pythagorean theorem
    const calculatedHypoLength = Math.sqrt(Math.pow(riseNum, 2) + Math.pow(runNum, 2));
    const roundedHypoLength = Math.round(calculatedHypoLength * 1000) / 1000;

    // Calculate comfort ratios
    const riseRunTotal = riseNum + runNum;
    const roundedRiseRunSum = Math.round(riseRunTotal * 100) / 100;
    
    const twoRiseRunTotal = (2 * riseNum) + runNum;
    const roundedTwoRiseRunSum = Math.round(twoRiseRunTotal * 100) / 100;

    setAngle(roundedAngle.toString());
    setHypoLength(roundedHypoLength.toString());
    setRiseRunSum(roundedRiseRunSum.toString());
    setTwoRiseRunSum(roundedTwoRiseRunSum.toString());
  };

  const hasResults = angle && hypoLength && riseRunSum && twoRiseRunSum;

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate Stair Angle
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find angles and hypotenuse from rise and run measurements
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="Rise"
                  type="number"
                  value={rise}
                  error={showError && (!rise || isNaN(parseFloat(rise)))}
                  onChange={(e) => setRise(e.target.value)}
                  inputProps={{ step: "0.001", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Vertical height of each step"
                />
                
                <TextField
                  fullWidth
                  label="Run"
                  type="number"
                  value={run}
                  error={showError && (!run || isNaN(parseFloat(run)))}
                  onChange={(e) => setRun(e.target.value)}
                  inputProps={{ step: "0.001", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Horizontal depth of each step"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {hasResults ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip 
                      label="Stair Angle" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Stair Angle" }}
                      fullWidth
                      value={`${angle}°`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box>
                    <Chip 
                      label="Hypotenuse Length" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Hypotenuse Length" }}
                      fullWidth
                      value={`${hypoLength} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Chip 
                      label={`Rise + Run (18" ideal)`} 
                      color="secondary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Rise + Run (18\" ideal)" }}
                      fullWidth
                      value={`${riseRunSum} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: Math.abs(parseFloat(riseRunSum!) - 18) <= 0.5 ? '#eef7f0' : '#fff4df',
                          color: 'text.primary',
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Ideal: 18" (comfort ratio)
                    </Typography>
                  </Box>

                  <Box>
                    <Chip 
                      label={`2(Rise) + Run (25" ideal)`} 
                      color="secondary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "2(Rise) + Run (25\" ideal)" }}
                      fullWidth
                      value={`${twoRiseRunSum} inches`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: Math.abs(parseFloat(twoRiseRunSum!) - 25) <= 0.5 ? '#eef7f0' : '#fff4df',
                          color: 'text.primary',
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Ideal: 25" (safety ratio)
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#edf5fc', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Formulas:</strong><br/>
                      • Angle = arctan(rise/run)<br/>
                      • Hypotenuse = √(rise² + run²)<br/>
                      • Comfort ratio = rise + run (ideal: 18")<br/>
                      • Safety ratio = 2(rise) + run (ideal: 25")
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter rise and run values to calculate stair angle
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="angle" 
              alt="Stair angle calculation diagram showing rise, run, and angle relationship" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default StairAngle;